import { useEffect, useState } from 'react'
import {
  useQuery,
  DocumentNode,
  ApolloError,
  OperationVariables
} from '@apollo/client'

interface UseFetchOnceOptions<TData, TVariables extends OperationVariables> {
  query: DocumentNode
  // True once Context already holds this Loader's data — skips a redundant
  // refetch on navigating back.
  skip: boolean
  variables?: TVariables
  onError?: (error: ApolloError) => void
  onData: (data: TData) => void
  // Only pass this if something outside the Loader needs to force a refetch
  // later — caller must run while this Loader is still mounted, see
  // CLAUDE.md's Loader pattern.
  onRefetch?: (refetch: () => Promise<unknown>) => void
  // Override when "ready" means more than just "not loading".
  isReady?: (loading: boolean) => boolean
  isParentLoaded: boolean
}

interface UseFetchOnceResult {
  isLoaded: boolean
  loading: boolean
}

export const useFetchOnce = <
  TData,
  TVariables extends OperationVariables = OperationVariables
>({
  query,
  skip,
  variables,
  onError,
  onData,
  onRefetch,
  isReady,
  isParentLoaded
}: UseFetchOnceOptions<TData, TVariables>): UseFetchOnceResult => {
  const [isLoaded, setIsLoaded] = useState(false)

  const { data, loading, refetch } = useQuery<TData, TVariables>(query, {
    skip,
    variables,
    onError
  })

  useEffect(() => {
    if (!onRefetch || !refetch) {
      return
    }

    // Forwarded uncalled. Callers typically store it via a setState setter
    // (setRefetchX(() => refetch)) — the wrapping is required, not optional:
    // a bare function passed to a setState setter is treated as a functional
    // updater and invoked immediately, firing the fetch now and storing the
    // resulting Promise instead of the function itself.
    onRefetch(refetch as unknown as () => Promise<unknown>)
  }, [refetch])

  useEffect(() => {
    if (data) {
      onData(data)
    }
  }, [data])

  useEffect(() => {
    const ready = isReady ? isReady(loading) : !loading
    setIsLoaded(isParentLoaded && ready)
  }, [loading, isParentLoaded])

  return { isLoaded, loading }
}
